import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { buildBigT } from '../entities/bigT.js';
import { HUD } from '../ui/hud.js';
import { addCrumbs, setFlag } from '../core/save.js';
import { flagObjective, acceptQuest, turnIn, isActive, isCompleted, isReady } from '../core/quests.js';
import { advanceTime } from '../core/world.js';
import { spendForAction } from '../core/actions.js';
import { GrillFlip } from '../minigames/grillFlip.js';

// Big T's Grill — a sizzling kitchen where Bread Boi works the flattop.

export function createGrillScene(){
  return () => new GrillScene();
}

const GRILL = new THREE.Vector3(0, 0, -3.2);

class GrillScene {
  constructor(){
    this.state = 'explore';
    this.game = null;
    this.panels = [];

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x2A1F1A);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 11;
    this.camTarget = new THREE.Vector3(0, 1.5, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildRoom();
    this._buildGrill();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 4);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.bigT = buildBigT();
    this.bigT.position.set(0, 0, -5.2);
    this.bigT.rotation.y = 0;
    this.scene.add(this.bigT);

    this.hotspots = [
      { pos: GRILL.clone(), r: 2.4, label: '🥩 work the flattop', action: () => this._startShift() },
      { pos: new THREE.Vector3(0, 0, 5.5), r: 2.2, label: '← back to town', action: () => this.manager.goTo('town', { from: 'grill' }) }
    ];
    this.nearSpot = null;
  }

  _buildRoom(){
    this.scene.add(new THREE.AmbientLight(0xFFE7C8, 0.55));
    const key = new THREE.DirectionalLight(0xFFF2D8, 0.65);
    key.position.set(3, 9, 6); key.castShadow = true; key.shadow.mapSize.set(1024, 1024);
    this.scene.add(key);
    const warm = new THREE.PointLight(0xF49B33, 0.6, 16); warm.position.set(0, 3, -2); this.scene.add(warm);

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(16, 16), new THREE.MeshLambertMaterial({ color: 0x6A4A3E }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);
    const wall = new THREE.Mesh(new THREE.PlaneGeometry(16, 8), new THREE.MeshLambertMaterial({ color: 0x4A3530 }));
    wall.position.set(0, 4, -7); wall.receiveShadow = true; this.scene.add(wall);

    const marquee = textSprite("Big T's Grill", 30, '#FFF4DF', 4.8, 1.1);
    marquee.position.set(0, 3.6, -6.7); this.scene.add(marquee);

    this.exitDoor = box(1.5, 2.4, 0.3, C.crustDark, 0, 1.2, 6.9, this.scene);
    const sign = textSprite('← to town', 24, '#FFF4DF', 2.8, 0.8);
    sign.position.set(0, 2.9, 6.7); this.scene.add(sign);
  }

  _buildGrill(){
    const g = new THREE.Group(); g.position.copy(GRILL); this.scene.add(g);
    // stainless counter
    box(5.5, 1.0, 1.4, 0x888888, 0, 0.5, 0, g);
    // grill surface
    box(5.2, 0.08, 1.1, 0x333333, 0, 1.04, 0, g);
    // heat glow underneath
    const glow = new THREE.Mesh(
      new THREE.PlaneGeometry(5.0, 1.0),
      new THREE.MeshBasicMaterial({ color: 0xFF5500, transparent: true, opacity: 0.25 })
    );
    glow.rotation.x = -Math.PI/2; glow.position.set(0, 1.09, 0); g.add(glow);
    this.grillGlow = glow;

    // grease trap / drip pan
    box(0.6, 0.2, 1.0, 0x555555, 2.4, 0.1, 0, g);
    // condiment bottles
    cyl(0.08, 0.08, 0.28, 0x9B2B1E, -2.0, 1.18, 0.4, g, 8);
    cyl(0.08, 0.08, 0.28, 0xF7C948, -2.25, 1.18, 0.35, g, 8);
  }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.3, 0.7];
    this._toExplore(true);
  }

  exit(){
    this._teardownGame();
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _toExplore(first){
    this.state = 'explore';
    this._clearPanels();
    this.bread.resetLimbs();
    this.input.onTap = (ndc, ray) => this._exploreTap(ray);
    HUD.setSubtitle("big t's grill");
    if (first) HUD.say("Big T's Grill smells like victory. Walk up to the flattop to work a shift.");
    if (!isActive('grill-first-flip') && !isCompleted('grill-first-flip')) acceptQuest('grill-first-flip');
    HUD.setHint('click to walk · walk up to the grill or the exit');
    HUD.hideHintSoon(7000);
  }

  _exploreTap(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-6.5, Math.min(6.5, gp.x));
    gp.z = Math.max(-5.4, Math.min(6.4, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  _startShift(){
    const check = spendForAction('grillFlip');
    if (!check.ok){
      HUD.say(`Too tired to grill right now. ${check.reason}`);
      return;
    }

    this.state = 'playing';
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null); HUD.setHint(''); HUD.hideHint(); HUD.say('');

    this.bread.stop();
    this.bread.root.position.set(0, 0, -1.6);
    this.bread.root.rotation.y = Math.PI;

    this.game = new GrillFlip({
      onFlip: () => { this.bread.drawFace('grin'); this.bigT.position.y = 0.05; setTimeout(() => this.bigT.position.y = 0, 120); },
      onPlate: () => { this.bread.drawFace('yum'); },
      onFinish: (r) => this._results(r)
    });
    this.game.mount();
    this.game.begin();

    const q = this._panel('<button class="cafe-quit">✕ leave</button>', 'bare');
    q.querySelector('.cafe-quit').onclick = () => { this._teardownGame(); this._toExplore(false); };
  }

  _results(r){
    this.state = 'results';
    this._teardownGame();
    this.bread.drawFace('yum');
    if (r.crumbs > 0) addCrumbs(r.crumbs);
    setFlag('flippedFirstPatty', true);
    flagObjective('grill-first-flip', 'flippedFirstPatty');
    if (isActive('grill-first-flip') && isReady('grill-first-flip')) turnIn('grill-first-flip');
    HUD.refreshCrumbs();

    const grade = r.rating >= 95 ? 'S' : r.rating >= 85 ? 'A' : r.rating >= 70 ? 'B' : r.rating >= 50 ? 'C' : 'D';
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">${grade}</div>
        <h2>Shift done!</h2>
        <div class="cafe-stats">
          <div><b>${r.plated}</b><span>plated</span></div>
          <div><b>${r.burnt}</b><span>burnt</span></div>
          <div><b>${r.raw}</b><span>raw</span></div>
          <div><b>${r.rating}%</b><span>rating</span></div>
        </div>
        <p class="cafe-payout">🍞 +${r.crumbs} crumbs</p>
        <button class="cafe-btn again">↻ another shift</button>
        <button class="cafe-btn ghost leave">← back to the floor</button>
      </div>`);
    p.querySelector('.again').onclick = () => this._startShift();
    p.querySelector('.leave').onclick = () => this._toExplore(false);
  }

  _teardownGame(){ this._clearPanels(); if (this.game){ this.game.unmount(); this.game = null; } }

  update(dt){
    if (this.grillGlow){
      this.grillGlow.material.opacity = 0.2 + Math.sin(performance.now() * 0.004) * 0.08;
    }

    if (this.state === 'playing'){
      this.game.update(dt);
      this.camera.position.set(3.2, 4.2, 5.8);
      this.camera.lookAt(0, 1.2, -2.5);
      return;
    }

    this.bread.update(dt);
    if (this.state === 'explore'){
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
      if (near && nd < near.r){
        if (this.nearSpot !== near){ this.nearSpot = near; HUD.showEnter(near.label, () => near.action()); }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.4, 1.5, this.bread.root.position.z * 0.4), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
